import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UsersRound, Library, Puzzle, Sparkles, ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

// 3-step tour shown to any user whose `onboarding_completed_at` is null.
// Backdrop-only modal — no route hijacking. "Skip" or "Get started" both mark it done.
const STEPS = [
  {
    icon: UsersRound,
    title: "Add your first learner",
    body:
      "Learners in Discrimina are PHI-minimised — just a chart name (initials) and any notes you want. Add as many as you need under the Learners tab.",
    cta: "Go to Learners",
    to: "/learners",
  },
  {
    icon: Library,
    title: "Import from the Starter Library",
    body:
      "Under Cards, tap Starter library. Grab a one-tap preset (Kitchen, Bathroom, Objects …) and every card comes with a Tact + LR twin, auto-illustrated in the background.",
    cta: "Go to Cards",
    to: "/cards",
  },
  {
    icon: Puzzle,
    title: "Present cards to your learner",
    body:
      "Head to Activities to present card arrays with drag-to-match, or run sorting drills by verbal operant. Discrimina is a pure card-design + presentation studio — no data collection, just clean stimulus delivery.",
    cta: "Open Activities",
    to: "/activities",
  },
];

export default function OnboardingTour() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) return;
    // Show only once per account — server flag is source of truth, local key
    // is a client-side belt-and-braces so we don't flash mid-session refresh.
    const localKey = `cardhive_onboarding_${user.id}`;
    if (user.onboarding_completed_at) {
      localStorage.setItem(localKey, "done");
      return;
    }
    if (localStorage.getItem(localKey) === "done") return;
    // Small delay so it doesn't jump on top of a route transition
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [user]);

  const finish = async (goTo) => {
    setOpen(false);
    if (user) localStorage.setItem(`cardhive_onboarding_${user.id}`, "done");
    try {
      await http.post("/auth/complete-onboarding");
      await refresh?.();
    } catch {
      // Non-blocking — the local flag prevents replay.
    }
    if (goTo) nav(goTo);
  };

  if (!open || !user) return null;
  const S = STEPS[step];
  const Icon = S.icon;
  const isBeta = !!user.beta_ends_at && new Date(user.beta_ends_at) > new Date();

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-tour">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="relative bg-slate-900 text-white px-6 pt-6 pb-8">
          <button
            onClick={() => finish(null)}
            className="absolute top-3 right-3 text-slate-400 hover:text-white"
            data-testid="onboarding-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400 font-bold">
            <Sparkles className="w-3.5 h-3.5" />
            {isBeta ? "Welcome to your beta" : "Welcome to Discrimina"} · step {step + 1} of {STEPS.length}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-extrabold leading-tight">{S.title}</h2>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-700 leading-relaxed">{S.body}</p>
          {isBeta && step === 0 && (
            <p className="mt-3 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              You're on beta access — Clinic plan free until {new Date(user.beta_ends_at).toLocaleDateString()}. Tap the Feedback button any time to shape what we build next.
            </p>
          )}
        </div>
        <div className="px-6 pb-6 flex items-center justify-between">
          <button
            onClick={() => finish(null)}
            className="text-sm text-slate-500 hover:text-slate-800"
            data-testid="onboarding-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-1.5" aria-hidden>
            {STEPS.map((s, i) => (
              <span
                key={s.title}
                className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-slate-900" : "bg-slate-200"}`}
              />
            ))}
          </div>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} className="rounded-full" data-testid="onboarding-next">
              Next <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={() => finish(S.to)} className="rounded-full" data-testid="onboarding-finish">
              {S.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
